/**
 * Shared contract for a GAM "Ad Slot" collection item (schema `adslot`) as it is
 * picked in authoring and later rendered through the Aditude wrapper.
 *
 * Both the Lexical article editor (inline placements) and the prelem
 * "GAM Ad Setting" (top/bottom placements) snapshot the same allow-listed fields
 * at pick time, so the published content carries its own render config and
 * never depends on a delivery-side collection fetch.
 */

/** `content_type` / `data-ad-type` of a GAM slot served through the Aditude wrapper. */
export const ADITUDE_AD_TYPE = "gam-aditude";

/** Collection tag under which the `adslot` schema is registered in the content gallery. */
export const AD_SLOT_COLLECTION_TAG = "GAM Ad Slots";

/** Reserved height for an Aditude placement whose ratio can't be resolved. */
export const DEFAULT_AD_MIN_HEIGHT = 90;

/**
 * Reserved height for a placement, derived from its authored ratio against a
 * nominal content width so the slot doesn't shift layout while the auction runs.
 *
 * Lives here rather than in a prelem library because `@platformx/prelems`
 * resolves to a different library per tenant branch (libs/prelems on kiwi_dev,
 * libs/prelems-etg on etg_dev) — both user-experience apps need this, so it has
 * to come from a library both branches share.
 */
export const ratioToMinHeight = (ratio?: string, width = 728): number => {
  const match = /^(\d+)\s*[x:]\s*(\d+)$/i.exec((ratio || "").trim());
  if (!match) return DEFAULT_AD_MIN_HEIGHT;
  const [, ratioWidth, ratioHeight] = match;
  const height = (width * Number(ratioHeight)) / Number(ratioWidth);
  return Number.isFinite(height) && height > 0 ? Math.round(height) : DEFAULT_AD_MIN_HEIGHT;
};

/**
 * Only these fields of an Ad Slot are ever read downstream. We snapshot ONLY
 * these, never the whole `authoring_getSchemaContent` document — that also
 * carries internal fields (author/editor, workflow status, tags, …) which would
 * otherwise leak into PUBLIC page source.
 */
export const AD_SLOT_SNAPSHOT_FIELDS = [
  "title",
  "base_div_id_x_str",
  "position_label_x_str",
  "placeholder_ratio_x_str",
  "collapse_if_empty_x_bool",
  "key_values_txt",
] as const;

export type AdSlotSnapshotField = (typeof AD_SLOT_SNAPSHOT_FIELDS)[number];

export type AdSlotSnapshot = Partial<Record<AdSlotSnapshotField, unknown>>;

/** One row of the ad-slot "Custom Targeting" repeater (`key_values_txt`). */
export interface AdSlotKeyValue {
  key: string;
  value: string;
}

/** The `key_values_txt` repeater caps at 10 rows in the `adslot` schema. */
export const AD_SLOT_MAX_KEY_VALUES = 10;

/**
 * Normalises the raw `key_values_txt` repeater value into clean `{key, value}`
 * rows: trims both sides, drops rows with an empty key (the repeater always
 * renders one blank row), and caps at the schema's row limit. Anything that is
 * not an array (missing field on legacy items, malformed data) yields `[]`.
 */
export const normalizeAdSlotKeyValues = (raw: unknown): AdSlotKeyValue[] => {
  if (!Array.isArray(raw)) return [];
  const rows: AdSlotKeyValue[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const key = String((item as Record<string, unknown>).key ?? "").trim();
    const value = String((item as Record<string, unknown>).value ?? "").trim();
    if (!key) continue;
    rows.push({ key, value });
    if (rows.length >= AD_SLOT_MAX_KEY_VALUES) break;
  }
  return rows;
};

/** Copies only the allow-listed render fields out of a fetched ad-slot document. */
export const pickAdSlotSnapshot = (detail: Record<string, unknown> | null | undefined) => {
  const picked: AdSlotSnapshot = {};
  if (!detail) return picked;
  for (const field of AD_SLOT_SNAPSHOT_FIELDS) {
    if (detail[field] !== undefined) picked[field] = detail[field];
  }
  if (picked.key_values_txt !== undefined) {
    picked.key_values_txt = normalizeAdSlotKeyValues(picked.key_values_txt);
  }
  return picked;
};

/** Normalises the `"true"`/`true` values a `*_x_bool` field round-trips as. */
export const adSlotBool = (value: unknown): boolean => value === true || value === "true";
